/**
 * Instance management service.
 * Fetches tenant/instance/user data from our own DB via the session endpoint.
 * Zero SPC dependency — all data sourced from tenant_instance_registry.
 */

import { createSession } from "../../services/authSessionService";
import { getInstanceId, saveInstanceId } from "../../utils/authService";
import type { SessionResponse } from "../../../shared/types/rbac";

// Session requests are deduplicated inside authSessionService, so the login
// callback and this module share a single /auth/session call per page load.
function getCachedSession(): Promise<SessionResponse> {
  return createSession();
}

type AccessibleInstance = SessionResponse["accessibleInstances"][number];

/**
 * Picks the instance this session is actually operating on.
 *
 * `getInstanceId()` falls back to localStorage, and that value outlives a
 * logout: sign in again against a different tenant and the stale id is still
 * there, but it is NOT in this session's accessibleInstances. Treating that
 * "not found" as "no tenant to filter by" is what let one tenant's name render
 * beside another tenant's instance — a pairing that does not exist.
 *
 * A requested instance this session cannot access is therefore discarded, not
 * honoured, falling back to the same default the app uses at startup
 * (`accessibleInstances[0]`, see useAppInitialization.getDefaultInstance).
 *
 * Pure and exported so the precedence is testable without a session or a DOM.
 */
export function resolveCurrentInstance(
  accessibleInstances: AccessibleInstance[],
  requestedInstanceId: string,
): { instance: AccessibleInstance | null; wasRequestedStale: boolean } {
  if (!accessibleInstances || accessibleInstances.length === 0) {
    return { instance: null, wasRequestedStale: !!requestedInstanceId };
  }

  const requested = requestedInstanceId
    ? accessibleInstances.find((i) => i.instanceId === requestedInstanceId)
    : undefined;

  if (requested) return { instance: requested, wasRequestedStale: false };

  return {
    instance: accessibleInstances[0],
    // Only "stale" if something was actually asked for and rejected. No saved
    // id at all is a first visit, not a cross-tenant leak.
    wasRequestedStale: !!requestedInstanceId,
  };
}

/** Resolves against the session and repairs a stale saved instance id. */
function resolveAndRepair(session: SessionResponse): AccessibleInstance | null {
  const { instance, wasRequestedStale } = resolveCurrentInstance(
    session.accessibleInstances,
    getInstanceId(),
  );
  // Leaving the dead id in localStorage means every later getInstanceId() call
  // re-derives the same wrong answer.
  if (wasRequestedStale && instance) saveInstanceId(instance.instanceId);
  return instance;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
import { grantsPlatformAdmin } from "../../../shared/roles";

/**
 * What `getCurrentUser()` returns. It is built here from the cached session —
 * nothing is fetched under this name — so this is the whole payload, not a
 * subset of some larger server shape.
 *
 * Declaring it matters: while this function was `Promise<any>`, three callers
 * each wrote their own guess at the shape (`useCurrentUser`, `account.tsx`,
 * `settings/tenants/tenants.tsx`), and two of those guesses named fields that
 * do not exist here — the mismatch PRs #981, #982 and #1015 each had to undo.
 */
export interface CurrentUser {
  /** `session.user.displayName`, or "" when the directory has no display name. */
  fullname: string;
  email: string;
  isPlatformAdmin: boolean;
  /** ISO 8601, stamped at call time — not a stored last-login. */
  lastLogin: string;
  /** Derived from `accessibleInstances`; `tenantid` is null for a global assignment. */
  tenants: Array<{ tenantid: string | null; instances: Array<{ instanceid: string }> }>;
}

export const getCurrentUser = async (): Promise<CurrentUser> => {
  const session = await getCachedSession();
  // Scope is known here — no tenant and no instance IS global — so this is the
  // precise test, not the name-only one. It also drops a globally-scoped `admin`,
  // matching accessResolutionService: a global assignment of just any role stopped
  // conferring platform reach when `skypoint_admin` was restored.
  const isPlatformAdmin = session.assignments.some(
    (a) => !a.tenantId && !a.instanceId && grantsPlatformAdmin(a.role.name, "global"),
  );
  return {
    fullname: session.user.displayName || "",
    email: session.user.email,
    isPlatformAdmin,
    lastLogin: new Date().toISOString(),
    tenants: session.accessibleInstances.map((inst) => ({
      tenantid: inst.tenantId,
      instances: [{ instanceid: inst.instanceId }],
    })),
  };
};

/**
 * The per-instance shape `getTenants()` builds for each tenant.
 *
 * Deliberately NOT `IInstance` from
 * `views/dashboard/header/tenants/tenants.interface.ts`: that declares
 * fourteen fields and this builds five. Naming the real shape here keeps the
 * gap visible instead of letting `any` paper over it.
 */
interface SessionTenantInstance {
  id: string;
  name: string;
  displayname: string;
  isactive: boolean;
  /** The viewer's own role in this instance, or null when the session has none. */
  roleName: string | null;
}

/**
 * NOTE ON THE RETURN TYPE. This stays `Promise<any>` on purpose, and the
 * honest annotation is not available yet.
 *
 * `useHeaderData` dispatches this result straight into `saveTenantsList`,
 * which is typed `PayloadAction<ITenantData>` — so the store believes it holds
 * `ITenant[]`. It does not. What this function builds differs from `ITenant`
 * in two ways:
 *
 *   - `datecreated` / `dateupdated` are ISO strings here; `ITenant` declares
 *     them as `Date`.
 *   - each instance carries the five fields above, where `IInstance` declares
 *     fourteen — `description`, `type`, `timezone`, `dataRefreshschedule`,
 *     `instancePrivacy`, `instanceHistory`, `datecreated`, `dateupdated`,
 *     `createdby` and `updatedby` are never populated.
 *
 * Annotating `Promise<ITenantData>` would therefore be a false claim that the
 * compiler would reject, which is the useful signal rather than a problem to
 * work around. No consumer reads the missing fields today — they read `id`,
 * `name`, `businessname`, `websiteurl` and `instances` — so this is a stale
 * declaration rather than a live bug, but it is the same shape that hid the
 * gate #982 had to fix. Resolving it means deciding whether the tenants slice
 * gets its own type or `IInstance` narrows, and `IInstance` is also used by
 * `settings/platform/instance/instance.tsx` for a different, fuller payload
 * (which it reaches via `as unknown as IInstance`, a cast that suggests the
 * same mismatch from the other side). That is a design decision, not a
 * substitution.
 */
export const getTenants = async (): Promise<any> => {
  const session = await getCachedSession();

  // Group accessible instances by tenantId
  const tenantMap = new Map<string, { tenantName: string; instances: SessionTenantInstance[] }>();

  for (const inst of session.accessibleInstances) {
    const tid = inst.tenantId || "default";
    if (!tenantMap.has(tid)) {
      tenantMap.set(tid, {
        tenantName: inst.tenantName || inst.operatorName || tid,
        instances: [],
      });
    }
    tenantMap.get(tid)!.instances.push({
      id: inst.instanceId,
      name: inst.instanceName || inst.instanceId,
      displayname: inst.instanceName || inst.instanceId,
      isactive: inst.status === "active",
      // The viewer's role here, so screens can tell where they may grant access.
      roleName: inst.role?.name ?? null,
    });
  }

  const tenants = Array.from(tenantMap.entries()).map(([tid, data]) => ({
    id: tid,
    name: data.tenantName,
    businessname: data.tenantName,
    websiteurl: "",
    isactive: true,
    isResourceCreated: true,
    owners: [],
    instances: data.instances,
    xdmdatabase: "",
    powerappsenvironmentid: "",
    isSkyPointDefaultSettings: false,
    stripeCustomerId: "",
    isSkyPointDefaultDB: false,
    apiEnable: false,
    datecreated: new Date().toISOString(),
    dateupdated: new Date().toISOString(),
    createdby: "",
    updatedby: "",
  }));

  // Determine the selected tenant from the resolved instance, so the tenant
  // shown always owns the instance shown.
  const currentInstance = resolveAndRepair(session);
  const selectedTenantId = currentInstance?.tenantId || tenants[0]?.id || "";
  const selectedTenantName =
    currentInstance?.tenantName || currentInstance?.operatorName || tenants[0]?.businessname || "";

  return {
    tenants,
    selectedTenant: { id: selectedTenantId, name: selectedTenantName },
  };
};

export const getInstance = async (): Promise<any> => {
  const session = await getCachedSession();

  const currentInstance = resolveAndRepair(session);
  const tenantId = currentInstance?.tenantId || null;

  // Scope strictly to the resolved instance's tenant. Never fall back to the
  // full list: an unscoped list is how instances from other tenants ended up
  // rendered under the current tenant's name.
  const tenantInstances = tenantId
    ? session.accessibleInstances.filter((i) => i.tenantId === tenantId)
    : [];

  const instances = tenantInstances.map((i) => ({
    id: i.instanceId,
    name: i.instanceName || i.instanceId,
    displayname: i.instanceName || i.instanceId,
  }));

  const tenantName = currentInstance?.tenantName || currentInstance?.operatorName || "";

  return {
    instances,
    // Resolved, not raw: the raw id may be the stale cross-tenant one.
    selectedInstanceId: currentInstance?.instanceId || instances[0]?.id || "",
    selectedInstanceName: currentInstance?.instanceName || instances[0]?.displayname || "",
    tenant: {
      id: tenantId || "",
      name: tenantName,
      businessname: tenantName,
    },
    instanceCultureName: "en-US",
    // No timezone: nothing stores one per instance, and the SPC-era endpoint that carried it is
    // gone. This used to answer "US/Pacific" for every tenant, so an Indian admin's 12:04 pm run
    // read as 11:34 pm the day before, and the HRIS sync time was labelled in a zone nobody
    // chose. Undefined lets `dateFormatter` fall back to the viewer's own zone, which is what its
    // own comment says it is for, and lets the HRIS card say plainly that the time is UTC.
    instanceDefault: { timezone: undefined },
    skyPointLinkConfiguration: {},
  };
};

/**
 * Logged when a user switches tenant or instance. The SPC-era endpoint behind
 * this is gone; the call sites in useTenantInstance.ts and
 * TenantInstanceSwitcher.tsx still await it, so it stays as a no-op rather
 * than rippling an await removal through both.
 */
export const changedInstanceLogCreate = async (_type: string): Promise<void> => {};
